/**
 * MobileGroupesList
 *
 * Mobile card-based replacement for the desktop GroupeCard grid.
 * Each groupe card shows its color, name, team, and the full list of members inside it.
 */
import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  Fade,
  Collapse,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Langs from "../../../app/lang/langs";

const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";

function contrastText(hex) {
  if (!hex || typeof hex !== "string") return "#fff";
  const clean = hex.replace("#", "");
  if (clean.length < 6) return "#fff";
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 160 ? "#1a1a1a" : "#fff";
}

function GroupeCard({ groupe, members, getTeamLabel, onEditGroup, onDeleteGroup, onAddUser, canEdit }) {
  const [expanded, setExpanded] = useState(false);
  const groupeColor = groupe.color || "#9e9e9e";
  const teamLabel = getTeamLabel?.(groupe.id_team) || "";
  const hasEditPerm = canEdit?.(groupe.id_team);
  const initials = (groupe.label?.[0] || "G").toUpperCase();

  return (
    <Card
      elevation={0}
      sx={{
        mb: 1.5,
        borderRadius: "16px",
        overflow: "hidden",
        bgcolor: "rgba(255,255,255,0.88)",
        backdropFilter: "blur(16px)",
        border: "1px solid rgba(0,0,0,0.07)",
        borderLeft: `4px solid ${groupeColor}`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.05), 0 1px 3px rgba(0,0,0,0.06)",
        transition: "box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 1.5 } }}>
        {/* Header row */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                width: 40, height: 40, borderRadius: "50%",
                bgcolor: groupeColor, color: contrastText(groupeColor),
                display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 700, fontSize: "1rem", flexShrink: 0,
                boxShadow: `0 2px 8px ${groupeColor}40`,
              }}
            >
              {initials}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, fontSize: "0.95rem", lineHeight: 1.3, color: groupeColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {groupe.label}
              </Typography>
              {teamLabel && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", opacity: 0.75 }}>
                  {teamLabel}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Right side: member count + expand button */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
            <Chip
              icon={<GroupsOutlinedIcon sx={{ fontSize: "0.85rem" }} />}
              label={members.length}
              size="small"
              variant="outlined"
              sx={{
                fontWeight: 700, fontSize: "0.75rem", height: 26, borderRadius: "10px",
                borderColor: `${groupeColor}50`, color: groupeColor,
                "& .MuiChip-icon": { color: groupeColor },
              }}
            />
            {members.length > 0 && (
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
                sx={{
                  width: 32, height: 32, borderRadius: "10px",
                  transition: TRANSITION_SMOOTH,
                  "&:hover": { backgroundColor: `${groupeColor}15` },
                }}
              >
                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Members list (collapsible) */}
        <Collapse in={expanded} timeout={250}>
          <Divider sx={{ mt: 1.5, mb: 0.5, opacity: 0.4, borderColor: "rgba(0,0,0,0.07)" }} />
          <List dense disablePadding sx={{ mt: 0.5 }}>
            {members.map((member, idx) => (
              <ListItem key={member.id || idx} disablePadding sx={{ py: 0.25 }}>
                <ListItemAvatar sx={{ minWidth: 36 }}>
                  <Avatar
                    sx={{
                      width: 28, height: 28, fontSize: "0.7rem", fontWeight: 700,
                      bgcolor: member.color || groupeColor,
                      color: contrastText(member.color || groupeColor),
                    }}
                  >
                    {((member.prenom?.[0] || "") + (member.nom?.[0] || "")).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`${member.prenom || ""} ${member.nom || ""}`.trim()}
                  secondary={member.poste || null}
                  slotProps={{
                    primary: { style: { fontSize: "0.82rem", fontWeight: 500, lineHeight: 1.3 } },
                    secondary: { style: { fontSize: "0.72rem", opacity: 0.65 } },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Collapse>

        {/* Action buttons */}
        {hasEditPerm && (
          <>
            <Divider sx={{ mt: 1.5, opacity: 0.35, borderColor: "rgba(0,0,0,0.06)" }} />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5, gap: 0.25 }}>
              <Tooltip title={<Langs str="ADD_USER" />}>
                <IconButton
                  size="small"
                  onClick={() => onAddUser?.(groupe)}
                  sx={{ minWidth: 44, minHeight: 44, borderRadius: "12px", transition: TRANSITION_SMOOTH, "&:hover": { backgroundColor: "rgba(255,144,21,0.08)", transform: "scale(1.08)" }, "&:active": { transform: "scale(0.95)" } }}
                >
                  <PersonAddAltOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={<Langs str="EDIT" />}>
                <IconButton
                  size="small"
                  onClick={() => onEditGroup?.(groupe)}
                  sx={{ minWidth: 44, minHeight: 44, borderRadius: "12px", transition: TRANSITION_SMOOTH, "&:hover": { backgroundColor: "rgba(255,144,21,0.08)", transform: "scale(1.08)" }, "&:active": { transform: "scale(0.95)" } }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={<Langs str="DELETE" />}>
                <IconButton
                  size="small"
                  onClick={() => onDeleteGroup?.(groupe)}
                  sx={{ minWidth: 44, minHeight: 44, borderRadius: "12px", transition: TRANSITION_SMOOTH, color: "error.main", "&:hover": { backgroundColor: "rgba(211,47,47,0.08)", transform: "scale(1.08)" }, "&:active": { transform: "scale(0.95)" } }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function MobileGroupesList({
  groupesList,
  collaboratorsList,
  getTeamLabel,
  onEditGroup,
  onDeleteGroup,
  onAddUser,
  canEdit,
}) {
  const [searchText, setSearchText] = useState("");

  const filteredGroups = useMemo(() => {
    const list = groupesList || [];
    if (!searchText.trim()) return list;
    const terms = searchText.toLowerCase().split(" ").filter(Boolean);
    return list.filter((groupe) => {
      const searchStr = [groupe.label, getTeamLabel?.(groupe.id_team)]
        .filter(Boolean).join(" ").toLowerCase();
      return terms.every((term) => searchStr.includes(term));
    });
  }, [groupesList, searchText, getTeamLabel]);

  const allCollabs = collaboratorsList || [];

  return (
    <Box sx={{ px: 2, pt: 1.5, pb: 2.5, height: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }}>
      {/* Search bar */}
      <TextField
        fullWidth
        size="small"
        placeholder="Rechercher un groupe..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          mb: 1,
          "& input": { fontSize: "16px" },
          "& .MuiOutlinedInput-root": {
            borderRadius: "14px",
            bgcolor: "rgba(255,255,255,0.75)",
            backdropFilter: "blur(12px)",
            transition: TRANSITION_SMOOTH,
            "& fieldset": { borderColor: "rgba(0,0,0,0.06)", transition: TRANSITION_SMOOTH },
            "&:hover fieldset": { borderColor: "rgba(0,0,0,0.15)" },
            "&.Mui-focused fieldset": { borderColor: "#FF9015", boxShadow: "0 0 0 3px rgba(255,144,21,0.15)" },
          },
        }}
      />

      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, px: 0.5, fontWeight: 500, opacity: 0.7 }}>
        {filteredGroups.length} groupe{filteredGroups.length !== 1 ? "s" : ""}
      </Typography>

      {/* Card list */}
      <Box
        sx={{
          flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", pb: 2, scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { background: "rgba(0,0,0,0.12)", borderRadius: 3 },
        }}
      >
        {filteredGroups.map((groupe, index) => {
          const members = allCollabs.filter(c => c.groupeIds && c.groupeIds.includes(groupe.value));
          return (
            <Fade in key={groupe.id || groupe.value} timeout={200 + index * 40}>
              <div>
                <GroupeCard
                  groupe={groupe}
                  members={members}
                  getTeamLabel={getTeamLabel}
                  onEditGroup={onEditGroup}
                  onDeleteGroup={onDeleteGroup}
                  onAddUser={onAddUser}
                  canEdit={canEdit}
                />
              </div>
            </Fade>
          );
        })}

        {filteredGroups.length === 0 && (
          <Fade in timeout={400}>
            <Box sx={{ textAlign: "center", py: 8 }}>
              <GroupsOutlinedIcon sx={{ fontSize: 48, color: "text.disabled", opacity: 0.3, mb: 1.5 }} />
              <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.5 }}>
                {searchText.trim() ? "Aucun groupe trouvé" : "Aucun groupe disponible"}
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>
    </Box>
  );
}
